<template>
    <div class="tile-content">
        <p v-if="description" class="stack-m">{{ description }}</p>
        <div class="stack-m">
            <div class="grid-item grid-item-half grid-item-break-half">
                <v-checkbox
                    :value="true"
                    :selected="hasInstruction"
                    :disabled="!anchorIsAvailable"
                    :data-tooltip="checkboxTooltipText"
                    @input="toggleInstruction"
                    :label="checkboxLabel"/>
            </div>
        </div>
        <template v-if="hasInstruction && hasAvailableMethods">
            <div class="grid-content">
                <div class="grid-item grid-item-full">
                    <inline-time-period
                        :fluid="true"
                        :available-period-types="methodsList"
                        :custom-period-type-labels="customMethodLabels"
                        :period-type="instruction.method"
                        :week-type="instruction.week"
                        :day="instruction.day"
                        :time="instruction.time"
                        :validate="getTimePeriodValidate()"
                        @update="updateTimePeriod"/>
                </div>
            </div>
        </template>
        <template v-if="hasInstruction && !hasAvailableMethods">
            <div class="grid-content">
                <div class="grid-item grid-item-full">
                    No available methods for selected plans
                </div>
            </div>
        </template>
    </div>
</template>

<script>
    import moment from 'moment';
    import _ from '@/common/helpers/index';
    import {vCheckbox} from '@/components/form/index';
    import InlineTimePeriod from '@/components/inline-time-period/inline-time-period';
    import PeriodAnchorInstructionModel from '@/common/models/period-anchor-instruction-model';

    export default {
        name: 'recurring-interval-form',
        components: {
            InlineTimePeriod,
            vCheckbox,
        },
        props: {
            description: {
                type: String,
                required: false,
                default: null,
            },
            emptyLabel: {
                type: String,
                required: true,
            },
            disabledLabel: {
                type: String,
                required: true,
            },
            checkboxLabel: {
                type: String,
                required: true,
            },
            model: {
                required: true,
                validator: value => value === PeriodAnchorInstructionModel,
            },
            instruction: {
                validator: value => !value || value instanceof PeriodAnchorInstructionModel,
            },
            validate: {
                type: Object,
                required: true,
            },
            availablePlanPeriods: {
                type: Array,
                required: true,
            },
        },
        computed: {
            customMethodLabels() {
                return {
                    [this.model.PeriodAnchorMethods.immediately]: 'Immediately (defaults to start date)',
                };
            },
            defaultDataByMethod() {
                return {
                    [this.model.PeriodAnchorMethods.dayOfMonth]: {
                        day: 1,
                    },
                    [this.model.PeriodAnchorMethods.dayOfWeek]: {
                        day: moment.weekdays()[0],
                    },
                };
            },
            hasInstruction() {
                return !!this.instruction;
            },
            hasPlans() {
                return !!this.availablePlanPeriods.length;
            },
            availableMonthAnchor() {
                return this.availablePlanPeriods.includes('month');
            },
            availableWeekAnchor() {
                return this.availablePlanPeriods.includes('week');
            },
            methodsList() {
                return Object.values(this.model.PeriodAnchorMethods)
                    .filter((method) => {
                        if (method === this.model.PeriodAnchorMethods.dayOfMonth
                            && !this.availableMonthAnchor) {
                            return false;
                        }
                        if (method === this.model.PeriodAnchorMethods.dayOfWeek
                            && !this.availableWeekAnchor) {
                            return false;
                        }
                        return true;
                    });
            },
            anchorIsAvailable() {
                return this.hasPlans && (this.availableMonthAnchor || this.availableWeekAnchor);
            },
            hasAvailableMethods() {
                return this.methodsList.length;
            },
            checkboxTooltipText() {
                if (!this.hasPlans) {
                    return this.emptyLabel;
                }
                if (!this.anchorIsAvailable) {
                    return this.disabledLabel;
                }
                return null;
            },
        },
        methods: {
            getTimePeriodValidate() {
                return {
                    period: {
                        ...this.validate,
                    },
                    periodType: this.validate.method,
                    weekType: this.validate.week,
                };
            },
            timeUnitToPlural(unit) {
                return _.capitalize(
                    _.pluralize(unit, this.instruction.duration),
                );
            },
            update(periodAnchorInstructionUpdates) {
                const periodAnchorInstruction = this.model.prepareInstruction({
                    ...this.instruction,
                    ...periodAnchorInstructionUpdates || {},
                });
                this.$emit('update', periodAnchorInstruction);
            },
            toggleInstruction() {
                if (this.hasInstruction) {
                    this.$emit('update', null);
                } else {
                    // create instruction with default data
                    const [method] = this.methodsList;
                    // eslint-disable-next-line new-cap
                    const instruction = new this.model({
                        ...this.defaultDataByMethod[method],
                        method,
                    }).getPayload();
                    this.$emit('update', instruction);
                }
            },
            updateTimePeriod({periodType, ...otherParams}) {
                const extendCurrentParams = this.instruction.method === periodType;
                this.update({
                    method: periodType,
                    ...this.defaultDataByMethod[periodType] || {},
                    ...extendCurrentParams ? this.instruction : {},
                    ...otherParams || {},
                });
            },
        },
    };
</script>
